// Схемы модуля `characters` — федерация с CharacterHub (IEC Hero), этап Ш7.
//
// Граница ответственности с `contracts/external-modules`:
//   там — ТРАНСПОРТ к партнёру (Partner API v1, whitelist исходящего, классы
//   отказов), здесь — НАШ tRPC-контракт: то, что видит клиент StudyHub.
// Смешивать нельзя: транспорт общий для всех федераций, а этот контракт —
// продуктовый и принадлежит одному модулю.
//
// Чего здесь НЕТ и почему:
//   • списка сессий — `character_sessions` синкается PowerSync, и клиент читает
//     его через `usePowerSyncQuery` (read-from-source-of-truth, CLAUDE.md §14а).
//     Дублирующая процедура `sessions.list` создала бы вторую копию правды;
//   • сообщений в нашей БД — их у нас нет вовсе (§3 федерации). История —
//     ПРОКСИ к партнёру, поэтому у неё курсор партнёра, а не наш.
import { z } from "zod";
import { isoDateTimeSchema, localeSchema, uuidSchema } from "../_shared/common.js";
import { OUTBOUND_MESSAGE_MAX_CHARS, OUTBOUND_SCENARIOS } from "../external-modules/outbound.js";

// ─── Учебные сценарии ───────────────────────────────────────────────────────

/**
 * Сценарий обучающей сессии. Переиспользует союз исходящего контекста, а не
 * заводит второй: сценарий — это ровно то, что уходит партнёру в
 * `learningContext.scenarioId`, и два независимых перечисления разъехались бы
 * ровно так же, как разъезжались реестры до модульной платформы.
 */
export const characterScenarioSchema = z.enum(OUTBOUND_SCENARIOS);
export type CharacterScenario = z.infer<typeof characterScenarioSchema>;

// ─── Состояние модуля для экрана входа ──────────────────────────────────────

/**
 * Почему нельзя связать аккаунт прямо сейчас.
 *
 * Разные причины — разные экраны: возраст ведёт к подтверждению профиля,
 * согласие — к документу, отсутствие сервиса — к баннеру аварии. Одна строка
 * «недоступно» превратила бы три разных действия пользователя в тупик.
 */
export const characterLinkBlockerSchema = z.enum([
	"age_not_confirmed",
	"under_minimum",
	"consent_required",
	"consent_outdated",
]);
export type CharacterLinkBlocker = z.infer<typeof characterLinkBlockerSchema>;

export const characterStatusOutputSchema = z.object({
	/** Связан ли аккаунт с партнёром прямо сейчас. */
	linked: z.boolean(),
	/**
	 * Была ли связь когда-либо. Отличается от `linked` смыслом для UI: «связь
	 * разорвана, сессии остались» — это не то же самое, что «никогда не входил».
	 */
	everLinked: z.boolean(),
	consent: z.object({
		granted: z.boolean(),
		needsReConsent: z.boolean(),
		/** Версия опубликованного документа; `null` — документ не опубликован. */
		currentVersion: z.string().nullable(),
		acceptedVersion: z.string().nullable(),
	}),
	ageGate: z.object({
		allowed: z.boolean(),
		minAge: z.number().int().positive(),
	}),
	/** Что мешает нажать «Начать»; пустой массив — ничего. */
	blockers: z.array(characterLinkBlockerSchema),
});
export type CharacterStatusOutput = z.infer<typeof characterStatusOutputSchema>;

// ─── Каталог ────────────────────────────────────────────────────────────────

/**
 * Вид каталога.
 *
 * `public` объявлен, но на MVP закрыт продуктовым решением (§18 п.2 федерации:
 * публичный UGC даёт максимум модерационного риска при минимуме учебной
 * ценности). Значение оставлено в схеме, чтобы включение было правкой ОДНОГО
 * места на сервере, а не расширением контракта задним числом.
 */
export const characterCatalogKindSchema = z.enum(["official", "mine", "public"]);
export type CharacterCatalogKind = z.infer<typeof characterCatalogKindSchema>;

export const characterOceanSchema = z
	.object({
		openness: z.number().min(0).max(1).optional(),
		conscientiousness: z.number().min(0).max(1).optional(),
		extraversion: z.number().min(0).max(1).optional(),
		agreeableness: z.number().min(0).max(1).optional(),
		neuroticism: z.number().min(0).max(1).optional(),
	})
	.partial();

export const characterCatalogItemSchema = z.object({
	/** Идентификатор партнёра — наших id у карточек нет, мы их не храним. */
	externalId: z.string().min(1),
	title: z.string(),
	summary: z.string().nullable(),
	description: z.string().nullable().optional(),
	avatarUrl: z.string().nullable(),
	tags: z.array(z.string()),
	/**
	 * Язык персонажа. Всегда `null`: колонки языка у карточки в Hero НЕТ, исходный
	 * язык определяется на лету при переводе и не персистится. Поле оставлено,
	 * потому что честное `null` лучше выдуманного значения, а появись колонка —
	 * заполнится без правки контракта. Фасета «язык персонажа» на витрине нет.
	 */
	locale: z.string().nullable(),
	ocean: characterOceanSchema.nullable().optional(),
	/**
	 * Счётчики партнёра. `null` — партнёр их не отдал (старый релиз Hero или
	 * деградация): витрина обязана в этом случае скрыть счётчик и сортировку
	 * «Популярные», а не рисовать нулями и не выдумывать рейтинг.
	 */
	stats: z
		.object({
			chats: z.number().int().nonnegative(),
		})
		.nullable(),
	/** Дата создания карточки у партнёра. Основа сортировки «Новые». */
	createdAt: isoDateTimeSchema.nullable(),
});
export type CharacterCatalogItem = z.infer<typeof characterCatalogItemSchema>;
export type CharacterOcean = z.infer<typeof characterOceanSchema>;

/**
 * Порядок выдачи каталога.
 *
 * Живёт в контракте, а не на клиенте: сортировка выполняется НА СТОРОНЕ ПАРТНЁРА
 * (курсор кодирует поле сортировки), поэтому произвольная строка с клиента —
 * это 400 от Hero, а не «отсортируем сами».
 */
export const characterCatalogSortSchema = z.enum(["popular", "new", "name"]);
export type CharacterCatalogSort = z.infer<typeof characterCatalogSortSchema>;

export const characterCatalogInputSchema = z.object({
	kind: characterCatalogKindSchema.default("official"),
	cursor: z.string().optional(),
	limit: z.number().int().min(1).max(50).default(24),
	/**
	 * Поисковый запрос. Серверный, а не клиентский фильтр по загруженной странице:
	 * клиентский искал бы по первым 24 карточкам и отвечал «не нашлось» о живом
	 * персонаже со второй страницы — ложноотрицательный ответ пользователю.
	 */
	q: z.string().trim().max(100).optional(),
	/** Фасет по slug'ам тегов партнёра. Семантика — AND (сужение). */
	tags: z.array(z.string()).max(5).optional(),
	sort: characterCatalogSortSchema.default("popular"),
});

export const characterCatalogOutputSchema = z.object({
	items: z.array(characterCatalogItemSchema),
	nextCursor: z.string().nullable(),
	/**
	 * Страница отдана из TTL-кэша, потому что партнёр недоступен. Витрина обязана
	 * честно это показывать: «данные могли устареть» вместо вида живого каталога.
	 */
	fromCache: z.boolean(),
});

// ─── Карточка персонажа ─────────────────────────────────────────────────────

/**
 * Одна карточка по внешнему id.
 *
 * Отдельной СТРАНИЦЫ персонажа у нас нет и не заводится (раздел приватный,
 * SEO-ценности нет, лишний хоп до запуска): процедура кормит превью-шторку,
 * которая открывается мгновенно на данных уже загруженной страницы каталога и
 * дотягивает полное описание сюда — со скелетоном внутри, не блокируя открытие.
 */
export const characterGetInputSchema = z.object({
	externalId: z.string().min(1),
});

/** Форма ровно та же, что у карточки каталога — второй формы карточки не заводим. */
export const characterGetOutputSchema = characterCatalogItemSchema;

// ─── Словарь тегов ──────────────────────────────────────────────────────────

/**
 * Локализованный тег партнёра.
 *
 * Своего словаря лейблов мы НЕ заводим: таксономия принадлежит Hero и меняется
 * без нас, а второй словарь рассинхронизируется с ней в первый же их релиз.
 * Фолбэк на отсутствующий перевод — сырой slug, и это делает партнёр.
 */
export const characterTagSchema = z.object({
	slug: z.string().min(1),
	/** Группа таксономии (`genre`/`setting`/`personality`/`topic`/…); `null` — вне групп. */
	category: z.string().nullable(),
	label: z.string(),
	/** Сколько карточек несёт тег — витрина сортирует чипы по нему. */
	count: z.number().int().nonnegative(),
});
export type CharacterTag = z.infer<typeof characterTagSchema>;

export const characterTagsOutputSchema = z.object({
	items: z.array(characterTagSchema),
});

// ─── Плитки сценариев Hero ──────────────────────────────────────────────────

/**
 * Готовая завязка разговора из Hero: персонаж + заголовок + первая реплика.
 *
 * ДРУГАЯ сущность, чем `characterScenarioSchema` выше: там 4 учебных ФОРМАТА
 * (как персонаж ведёт себя педагогически), здесь — с чего начинается разговор.
 * Сущности ортогональны и совместимы в одной сессии.
 *
 * `firstUserMessage` приходит наружу затем, чтобы клиент ПРЕДЗАПОЛНИЛ им
 * композер, а не отправил автоматически: текст уходит стороннему сервису, и
 * человек обязан видеть, что именно уходит, до нажатия «Отправить».
 */
export const characterScenarioTileSchema = z.object({
	id: z.string().min(1),
	title: z.string(),
	firstUserMessage: z.string(),
	tags: z.array(z.string()),
	character: z.object({
		externalId: z.string().min(1),
		title: z.string(),
		avatarUrl: z.string().nullable(),
	}),
});
export type CharacterScenarioTile = z.infer<typeof characterScenarioTileSchema>;

export const characterScenarioTilesInputSchema = z.object({
	limit: z.number().int().min(1).max(12).default(6),
});

export const characterScenarioTilesOutputSchema = z.object({
	items: z.array(characterScenarioTileSchema),
});

// ─── Сессия ─────────────────────────────────────────────────────────────────

export const characterSessionSchema = z.object({
	id: uuidSchema,
	/** Чат на стороне партнёра — SoT содержимого. */
	externalSessionId: z.string(),
	characterExternalId: z.string(),
	characterTitle: z.string(),
	characterAvatarUrl: z.string().nullable(),
	scenario: characterScenarioSchema,
	locale: localeSchema,
	/** Материалы StudyHub, из которых собирается `learningContext` каждого хода. */
	materialIds: z.array(uuidSchema),
	/** Тема курса, из которой создана сессия (`null` — самостоятельный вход). */
	subsectionId: uuidSchema.nullable(),
	messageCount: z.number().int().nonnegative(),
	lastMessageAt: isoDateTimeSchema.nullable(),
	createdAt: isoDateTimeSchema,
});
export type CharacterSession = z.infer<typeof characterSessionSchema>;

export const characterCreateSessionInputSchema = z.object({
	characterExternalId: z.string().min(1),
	scenario: characterScenarioSchema,
	/** Пусто — сессия без материалов (свободный разговор). */
	materialIds: z.array(uuidSchema).max(20).default([]),
	subsectionId: uuidSchema.optional(),
	title: z.string().max(200).optional(),
	/**
	 * Осознанное «Начать новый разговор» — обойти дедупликацию.
	 *
	 * Нужен с тех пор, как дедуп распространился на разговоры БЕЗ материалов
	 * (2026-08-18). До этого свободные разговоры не дедуплицировались вовсе, и
	 * каждое нажатие «Начать» плодило пустой чат: витрина владельца показывала
	 * двух одинаковых Йод по три сообщения. Но склеивать их молча тоже нельзя —
	 * начать с персонажем новый разговор законное желание, и отличить его от
	 * случайного повторного нажатия может только сам человек, кнопкой.
	 */
	forceNew: z.boolean().default(false),
});

export const characterDeleteSessionInputSchema = z.object({ id: uuidSchema });

/**
 * Удаление сессии удаляет ТОЛЬКО метаданные StudyHub. Чат в Hero остаётся: он
 * принадлежит партнёру и пользователь мог им пользоваться там. Флаг возвращается
 * наружу, чтобы копирайт удаления не обещал невозможного.
 */
export const characterDeleteSessionOutputSchema = z.object({
	ok: z.literal(true),
	partnerChatPreserved: z.literal(true),
});

// ─── История сообщений (прокси к партнёру) ──────────────────────────────────

export const characterMessageSchema = z.object({
	id: z.string(),
	role: z.enum(["user", "assistant"]),
	text: z.string(),
	createdAt: isoDateTimeSchema,
});
export type CharacterMessage = z.infer<typeof characterMessageSchema>;

export const characterMessagesInputSchema = z.object({
	sessionId: uuidSchema,
	cursor: z.string().optional(),
	limit: z.number().int().min(1).max(100).default(50),
});

export const characterMessagesOutputSchema = z.object({
	items: z.array(characterMessageSchema),
	nextCursor: z.string().nullable(),
});

// ─── Стрим ──────────────────────────────────────────────────────────────────

export const characterStreamAudioSchema = z.object({
	mimeType: z.string().min(1).max(128),
	base64: z.string().min(1).max(3_000_000),
});

export const characterPrepareStreamInputSchema = z
	.object({
		sessionId: uuidSchema,
		/** Текст пользователя. Может быть пустым, если передан `audio`. */
		message: z.string().max(OUTBOUND_MESSAGE_MAX_CHARS).default(""),
		/** Голосовое сообщение — партнёр принимает как вход хода (не STT→текст). */
		audio: characterStreamAudioSchema.optional(),
	})
	.superRefine((val, ctx) => {
		if (!val.message.trim() && !val.audio) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "MESSAGE_OR_AUDIO_REQUIRED",
				path: ["message"],
			});
		}
	});

export const characterPrepareStreamOutputSchema = z.object({
	/** Одноразовый HMAC-токен для `POST /api/characters/stream`. */
	token: z.string(),
	/**
	 * Предупреждения, которые обязан показать клиент ДО отправки.
	 * `pii` — в тексте найдены персональные данные: не блокируем (пользователь
	 * вправе написать своё имя), но он должен понимать, что текст уходит вовне.
	 */
	warnings: z.array(z.enum(["pii"])),
});

/** Токен для приветствия — квоту чата не списывает. */
export const characterPrepareGreetingInputSchema = z.object({
	sessionId: uuidSchema,
});

export const characterPrepareGreetingOutputSchema = characterPrepareStreamOutputSchema;

// ─── Deep link в Hero ───────────────────────────────────────────────────────

export const characterDeepLinkInputSchema = z.object({
	/** Путь внутри партнёра; одноразовый код привязывается именно к нему. */
	target: z.enum(["home", "create-character", "session"]),
	sessionId: uuidSchema.optional(),
});

export const characterDeepLinkOutputSchema = z.object({
	url: z.string().url(),
	expiresAt: isoDateTimeSchema,
});

// ─── Связывание ─────────────────────────────────────────────────────────────

/**
 * Согласие принято. Версия возвращается наружу, чтобы экран показал ИМЕННО ту,
 * под которую подписались, а не перечитывал состояние гонкой.
 */
export const characterAcceptConsentOutputSchema = z.object({
	version: z.string(),
});

export const characterLinkOutputSchema = z.object({
	externalUserId: z.string(),
	/** true — связь уже существовала (повторное «Начать» со второго устройства). */
	existing: z.boolean(),
});

export const characterUnlinkOutputSchema = z.object({ ok: z.literal(true) });
